/* ═══════════════════════════════════════════════════════════════
   Lokale Kopie des Kellerbuchs — in IndexedDB

   Bis Oktober 2026 lag sie im localStorage. Der fasst je Herkunft
   rund 5 MB, und ein großer Keller kam dem nahe. Scheiterte das
   Schreiben, wurde der Fehler verschluckt und die ALTE Kopie blieb
   liegen — ohne Netz Getipptes war nach dem Neuladen weg, ohne dass
   es jemand bemerkte.

   Jetzt:
     - IndexedDB (Kontingent im Bereich von Hunderten MB), eigene
       Datenbank `winelake-lokal`, je Konto ein Eintrag.
     - Der Wetterteil liegt getrennt (Speicher `wetter`) — er ändert
       sich selten und muss nicht bei jedem Speichern mitgeschrieben
       werden (kern/wetterteil.js).
     - Ist IndexedDB nicht nutzbar, wird wie früher in den
       localStorage geschrieben. Jeder Eintrag trägt `zeit`; beim Laden
       gewinnt der jüngere, falls beide Wege einmal benutzt wurden.
     - `speichereLokal` meldet, ob es geklappt hat. Die App zeigt einen
       Fehlschlag an, statt ihn zu verschlucken.
     - Die alte localStorage-Kopie wird beim ersten erfolgreichen
       Schreiben in IndexedDB entfernt (Umzug ohne eigenen Schritt).
   ═══════════════════════════════════════════════════════════════ */

import { lokalSchluessel } from "../kern/api.js";
import { ohneWetter } from "../kern/wetterteil.js";

const DB_NAME = "winelake-lokal";
const KOPIE = "kopie";
const WETTER = "wetter";

let verbindung = null;

function oeffne() {
  if (verbindung) return verbindung;
  verbindung = new Promise((fertig, fehler) => {
    if (typeof indexedDB === "undefined") { fehler(new Error("IndexedDB steht nicht zur Verfügung.")); return; }
    const anfrage = indexedDB.open(DB_NAME, 1);
    anfrage.onupgradeneeded = () => {
      const d = anfrage.result;
      if (!d.objectStoreNames.contains(KOPIE)) d.createObjectStore(KOPIE);
      if (!d.objectStoreNames.contains(WETTER)) d.createObjectStore(WETTER);
    };
    anfrage.onsuccess = () => fertig(anfrage.result);
    anfrage.onerror = () => fehler(anfrage.error || new Error("IndexedDB konnte nicht geöffnet werden."));
    anfrage.onblocked = () => fehler(new Error("IndexedDB ist blockiert."));
  });
  verbindung.catch(() => { verbindung = null; });
  return verbindung;
}

function vorgang(laden, modus, arbeit) {
  return oeffne().then((d) => new Promise((fertig, fehler) => {
    const t = d.transaction(laden, modus);
    const anfrage = arbeit(t.objectStore(laden));
    t.oncomplete = () => fertig(anfrage ? anfrage.result : undefined);
    t.onerror = () => fehler(t.error);
    t.onabort = () => fehler(t.error || new Error("Vorgang abgebrochen."));
  }));
}

/* Zeitstempel, der auch bei zwei Schreibvorgängen in derselben
   Millisekunde streng wächst. */
let letzteZeit = 0;
const naechsteZeit = () => { letzteZeit = Math.max(Date.now(), letzteZeit + 1); return letzteZeit; };

function ausLocalStorage(benutzername) {
  try {
    const roh = localStorage.getItem(lokalSchluessel(benutzername));
    if (!roh) return null;
    const e = JSON.parse(roh);
    return e && e.daten ? { ...e, zeit: e.zeit || 0 } : null;
  } catch (e) { return null; }
}

/** { version, daten, ungesichert } oder null. */
async function ladeLokal(benutzername) {
  if (!benutzername) return null;
  let ausIdb = null;
  try {
    await kette;
    const e = await vorgang(KOPIE, "readonly", (s) => s.get(benutzername));
    if (e && typeof e.text === "string") {
      ausIdb = { version: e.version || 0, ungesichert: !!e.ungesichert, zeit: e.zeit || 0, daten: JSON.parse(e.text) };
    }
  } catch (e) { /* IndexedDB nicht nutzbar — dann zählt der localStorage */ }
  const alt = ausLocalStorage(benutzername);
  const sieger = !ausIdb ? alt : !alt ? ausIdb : (alt.zeit > ausIdb.zeit ? alt : ausIdb);
  if (!sieger) return null;
  return { version: sieger.version || 0, daten: sieger.daten, ungesichert: !!sieger.ungesichert };
}

async function hatLokal(benutzername) {
  return !!(await ladeLokal(benutzername));
}

/* Schreibvorgänge in Folge — ein älterer Stand darf einen jüngeren
   nicht überholen. */
let kette = Promise.resolve();

/** Legt den Stand ab. `text` ist optional die schon serialisierte
    Fassung OHNE Wetterteil (spart ein zweites JSON.stringify).
    Liefert ein Promise auf true (abgelegt) oder false. */
function speichereLokal(benutzername, { version = 0, daten, ungesichert = false }, text = null) {
  if (!benutzername || daten == null) return Promise.resolve(false);
  const zeit = naechsteZeit();
  let inhalt = text;
  if (inhalt == null) {
    try { inhalt = JSON.stringify(ohneWetter(daten)); } catch (e) { return Promise.resolve(false); }
  }
  const lauf = kette.then(async () => {
    try {
      await vorgang(KOPIE, "readwrite", (s) => s.put({ version, ungesichert: !!ungesichert, zeit, text: inhalt }, benutzername));
      /* Umzug: Die alte Kopie im localStorage ist jetzt überholt. */
      try { localStorage.removeItem(lokalSchluessel(benutzername)); } catch (e) {}
      return true;
    } catch (e) {
      /* Rückfall wie früher — mit dem Unterschied, dass ein Fehlschlag
         gemeldet wird. */
      try {
        localStorage.setItem(lokalSchluessel(benutzername),
          `{"version":${Number(version) || 0},"ungesichert":${!!ungesichert},"zeit":${zeit},"daten":${inhalt}}`);
        return true;
      } catch (x) {
        return false;
      }
    }
  });
  kette = lauf.catch(() => false);
  return lauf;
}

/** Wetterteil getrennt — { weatherCache, klimaCache } oder null. */
async function ladeWetter(benutzername) {
  if (!benutzername) return null;
  try {
    const e = await vorgang(WETTER, "readonly", (s) => s.get(benutzername));
    return e && typeof e.text === "string" ? JSON.parse(e.text) : null;
  } catch (e) { return null; }
}

function speichereWetter(benutzername, teil) {
  if (!benutzername || !teil) return Promise.resolve(false);
  let text;
  try { text = JSON.stringify(teil); } catch (e) { return Promise.resolve(false); }
  const lauf = kette.then(() => vorgang(WETTER, "readwrite", (s) => s.put({ text }, benutzername))
    .then(() => true, () => false));
  kette = lauf.catch(() => false);
  return lauf;
}

export { hatLokal, ladeLokal, ladeWetter, speichereLokal, speichereWetter };
